import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, MapPin, ShieldCheck, CheckCircle2, Star, Loader2, BadgeCheck, Navigation, Search, X } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BRAND } from '../lib/siteConfig';
import { localServiceSchema, faqSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { getRef } from '../lib/attribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARE_NEEDS = [
  'Skilled Nursing', 'Home Health Aide', 'Companion Care', 'Post-surgery care',
  'Dementia & Alzheimer\u2019s care', 'Physical therapy', 'Hospice support', 'Other',
];

const LeadCaptureBar = ({ city, st, defaultZip }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', care_need: 'Skilled Nursing', zip_code: defaultZip || '' });
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => { if (defaultZip) setForm((f) => (f.zip_code ? f : { ...f, zip_code: defaultZip })); }, [defaultZip]);

  if (dismissed) return null;

  const submit = async (e) => {
    e.preventDefault();
    const zip = (form.zip_code || '').replace(/\D/g, '').slice(0, 5);
    if (!form.name.trim()) { toast.error('Please add your name'); return; }
    if (zip.length < 5) { toast.error('Enter a valid 5-digit ZIP'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/leads`, {
        name: form.name.trim(), care_need: form.care_need, zip_code: zip,
        city, state: st, source: 'local_seo', ref: getRef() || 'local_seo',
      });
      toast.success('Got it! Create your account to see your matches.');
      navigate(`/auth?role=patient&need=${encodeURIComponent(form.care_need)}&zip=${zip}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 bg-cn-primary text-white border-t border-white/10"
      data-testid="lead-capture-bar"
    >
      <form onSubmit={submit} className="cn-container py-3 flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <p className="font-display font-semibold leading-tight">Get matched in {city}</p>
          <p className="text-xs text-white/70 hidden sm:block">Verified, background-checked clinicians — usually within minutes.</p>
        </div>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your name"
          aria-label="Your name"
          className="cn-input !w-40 !bg-white/10 !border-white/20 !text-white placeholder:!text-white/50"
          data-testid="lead-name-input"
        />
        <Select value={form.care_need} onValueChange={(v) => setForm({ ...form, care_need: v })}>
          <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white" data-testid="lead-need-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CARE_NEEDS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <input
          value={form.zip_code} inputMode="numeric" maxLength={5}
          onChange={(e) => setForm({ ...form, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) })}
          placeholder="ZIP"
          aria-label="ZIP code"
          className="cn-input !w-24 !bg-white/10 !border-white/20 !text-white placeholder:!text-white/50"
          data-testid="lead-zip-input"
        />
        <button type="submit" disabled={submitting} className="cn-btn bg-white text-cn-primary hover:bg-white/90 !py-2.5 disabled:opacity-60" data-testid="lead-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Get matched
        </button>
        <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss" className="text-white/60 hover:text-white p-1" data-testid="lead-bar-dismiss">
          <X className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};


const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia',
};

const TopClinicianCard = ({ c, rank }) => (
  <div className="cn-card cn-card-hover rounded-3xl p-6 relative flex flex-col" data-testid={`top-clinician-${c.id}`}>
    <span
      className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-cn-primary text-white text-sm font-bold flex items-center justify-center font-display shadow-sm"
      data-testid={`top-clinician-rank-${rank}`}
    >
      {rank}
    </span>
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-cn-soft flex-shrink-0">
        {c.photo_url
          ? <img src={c.photo_url} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-cn-primary font-semibold text-xl">{(c.name || '?')[0]}</div>}
      </div>
      <div className="min-w-0">
        <div className="font-display font-semibold text-cn-text truncate">{c.name}</div>
        <div className="text-sm text-cn-text-muted">{c.license_type} · ${c.hourly_rate}/hr</div>
        {c.rating ? (
          <div className="text-xs text-cn-text-muted inline-flex items-center gap-1 mt-0.5">
            <Star className="w-3 h-3 fill-cn-accent text-cn-accent" /> {c.rating} ({c.review_count})
          </div>
        ) : null}
      </div>
    </div>

    {/* Proximity — the Module IV signal */}
    <div
      className="mt-4 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-cn-accent/10 text-cn-primary text-xs font-semibold"
      data-testid={`top-clinician-proximity-${c.id}`}
    >
      <Navigation className="w-3.5 h-3.5" />
      {c.proximity_label}
    </div>

    {c.specialties?.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-1.5">
        {c.specialties.slice(0, 3).map((s) => (
          <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-cn-soft text-cn-text">{s}</span>
        ))}
      </div>
    )}

    <div className="mt-3 flex flex-wrap gap-1.5">
      <span className="cn-trust-badge text-xs"><ShieldCheck className="w-3 h-3" /> Verified</span>
      {['passed', 'cleared'].includes(c.background_check_status) && (
        <span className="cn-trust-badge text-xs"><BadgeCheck className="w-3 h-3" /> BG checked</span>
      )}
    </div>

    <div className="mt-5 flex gap-2 pt-4 border-t border-cn-border">
      <Link to={`/auth?role=patient`} className="cn-btn cn-btn-primary flex-1 justify-center text-sm" data-testid={`top-clinician-book-${c.id}`}>
        Book
      </Link>
      <Link to={`/clinicians/${c.slug || c.id}`} className="cn-btn cn-btn-ghost flex-1 justify-center text-sm" data-testid={`top-clinician-view-${c.id}`}>
        View profile
      </Link>
    </div>
  </div>
);

const LocalClinicianCard = ({ c }) => (  <Link to={`/clinicians/${c.id}`} className="cn-card cn-card-hover rounded-3xl p-5 block group" data-testid={`local-clinician-${c.id}`}>
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-cn-soft flex-shrink-0">
        {c.photo_url
          ? <img src={c.photo_url} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-cn-primary font-semibold">{(c.name || '?')[0]}</div>}
      </div>
      <div className="min-w-0">
        <div className="font-display font-semibold text-cn-text truncate">{c.name}</div>
        <div className="text-sm text-cn-text-muted">{c.license_type} · ${c.hourly_rate}/hr</div>
        {c.rating ? (
          <div className="text-xs text-cn-text-muted inline-flex items-center gap-1 mt-0.5">
            <Star className="w-3 h-3 fill-cn-accent text-cn-accent" /> {c.rating} ({c.review_count})
          </div>
        ) : null}
      </div>
    </div>
    {c.specialties?.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-1.5">
        {c.specialties.slice(0, 3).map((s) => (
          <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-cn-soft text-cn-text">{s}</span>
        ))}
      </div>
    )}
    <div className="mt-3 flex flex-wrap gap-1.5">
      <span className="cn-trust-badge text-xs"><ShieldCheck className="w-3 h-3" /> Verified</span>
      {['passed', 'cleared'].includes(c.background_check_status) && (
        <span className="cn-trust-badge text-xs"><BadgeCheck className="w-3 h-3" /> BG checked</span>
      )}
    </div>
  </Link>
);

export default function LocalCarePage() {
  const { state, city: citySlug } = useParams();
  const slug = `${(state || '').toLowerCase()}/${(citySlug || '').toLowerCase()}`;
  const [meta, setMeta] = useState(null);       // { city, state, count, ... }
  const [clinicians, setClinicians] = useState([]);
  const [topClinicians, setTopClinicians] = useState([]);  // Module IV — top 3 closest verified
  const [zipInput, setZipInput] = useState('');
  const [zipApplied, setZipApplied] = useState(null);      // ZIP-level precision refinement
  const [zipLoading, setZipLoading] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  const fetchTop = useCallback(async (zip) => {
    const params = { limit: 3 };
    if (zip) params.zip = zip;
    const res = await axios.get(`${API}/public/local/${state}/${citySlug}/top-clinicians`, { params });
    setTopClinicians(res.data?.clinicians || []);
    return res.data;
  }, [state, citySlug]);

  const applyZip = async (e) => {
    e.preventDefault();
    const z = zipInput.replace(/\D/g, '').slice(0, 5);
    if (z.length < 3) { toast.error('Enter a valid ZIP code'); return; }
    setZipLoading(true);
    try {
      const data = await fetchTop(z);
      setZipApplied(data?.zip || z);
    } catch {
      toast.error('Could not rank clinicians for that ZIP');
    } finally { setZipLoading(false); }
  };

  const clearZip = async () => {
    setZipInput('');
    setZipApplied(null);
    try { await fetchTop(null); } catch { /* noop */ }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const citiesRes = await axios.get(`${API}/public/cities`);
        const match = (citiesRes.data || []).find((c) => c.slug === slug);
        if (!match) { if (active) setStatus('notfound'); return; }
        if (active) setMeta(match);
        const [clinRes, topRes] = await Promise.all([
          axios.get(`${API}/public/clinicians`, { params: { city: match.city, state: match.state } }),
          axios.get(`${API}/public/local/${state}/${citySlug}/top-clinicians`, { params: { limit: 3 } }),
        ]);
        if (active) {
          setClinicians(clinRes.data || []);
          setTopClinicians(topRes.data?.clinicians || []);
          setStatus('ok');
        }
      } catch {
        if (active) setStatus('notfound');
      }
    })();
    return () => { active = false; };
  }, [slug, state, citySlug]);

  if (status === 'notfound') return <Navigate to="/home-care" replace />;

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-cn-bg">
        <PublicNavbar />
        <div className="cn-container flex items-center gap-2 text-cn-text-muted py-32 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading local care…
        </div>
        <Footer />
      </div>
    );
  }

  const city = meta.city;
  const st = meta.state;
  const stateName = STATE_NAMES[st] || st;
  const path = `/home-care/${slug}`;
  const specialties = meta.top_specialties || [];

  const faqs = [
    { q: `How do I find in-home care in ${city}, ${st}?`, a: `Tell CaraLoom what care you need and your location. We match you with verified, background-checked clinicians serving ${city} and surrounding areas — usually within minutes — and you can compare rates and reviews before booking.` },
    { q: `Are CaraLoom clinicians in ${city} licensed and screened?`, a: `Yes. Every clinician completes identity verification, primary-source license validation, and multi-state background screening, with continuous license monitoring.` },
    { q: `How much does home care cost in ${city}?`, a: `Rates vary by role and acuity. ${city} clinicians on CaraLoom start around $${meta.min_rate || 35}/hour, with transparent pricing shown up front and no hidden agency markups.` },
    { q: `How quickly can care start in ${city}?`, a: `Many ${city} families are matched within minutes and begin care within 24–48 hours, depending on local availability.` },
  ];

  const metaDescription = `Find verified, background-checked in-home nurses, aides, and caregivers in ${city}, ${stateName}. Compare local clinicians, transparent rates, and reviews on CaraLoom — then book directly.`;
  const schema = [
    localServiceSchema({ city, state: stateName, path, description: metaDescription }),
    faqSchema(faqs),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Home care near you', path: '/home-care' },
      { name: `${city}, ${st}`, path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="local-care-page">
      <Seo
        title={`Verified Home Care in ${city}, ${st} | RNs, Aides & Caregivers | ${BRAND}`}
        description={metaDescription}
        path={path}
        keywords={[`home care ${city}`, `nurse near me ${city}`, `caregiver ${city} ${st}`, `in-home care ${city}`]}
        schema={schema}
      />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/services">Home care near you</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{city}, {st}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero */}
      <section className="cn-container pt-8 pb-12">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-cn-accent" /> {city}, {stateName}</div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5">
            Verified Home Care in {city}, {st}
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">
            Looking for trusted in-home care in {city}? CaraLoom connects {city} families with{' '}
            <span className="text-cn-text font-medium">{meta.count} verified, background-checked</span> nurses, home health
            aides, and caregivers — matched to your needs, schedule, and budget. Compare real profiles and transparent rates,
            then book directly with the clinician you trust.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/auth" className="cn-btn cn-btn-primary" data-testid="local-get-started">Get matched in {city} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/patient/find-clinicians" className="cn-btn cn-btn-ghost" data-testid="local-browse-all">Browse all clinicians</Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> Background-checked</span>
            <span className="cn-trust-badge"><CheckCircle2 className="w-3.5 h-3.5" /> License-verified</span>
          </div>
        </div>
      </section>

      {/* Top 3 Closest Verified Clinicians — Module IV: Local Liquidity */}
      {topClinicians.length > 0 && (
        <section className="cn-container pb-14" data-testid="top-clinicians-section">
          <div className="flex items-center gap-2 mb-2">
            <Navigation className="w-4 h-4 text-cn-accent" />
            <span className="cn-eyebrow">{zipApplied ? `Closest to ${zipApplied}` : `Closest to ${city}`}</span>
          </div>
          <h2 className="cn-display text-2xl sm:text-3xl text-cn-text mb-2">
            Top {topClinicians.length} closest verified clinicians
          </h2>
          <p className="text-cn-text-muted mb-5 max-w-2xl">
            {zipApplied
              ? <>Ranked by exact distance from ZIP <span className="text-cn-text font-medium">{zipApplied}</span>. Every clinician is license-verified and background-checked — your nearest available care, first.</>
              : <>Ranked by proximity to {city}, {st}. Every clinician below is license-verified and background-checked — the nearest available care, first.</>}
          </p>

          {/* ZIP-level precision refine */}
          <form onSubmit={applyZip} className="flex flex-wrap items-center gap-2 mb-7" data-testid="zip-refine-form">
            <div className="relative">
              <MapPin className="w-4 h-4 text-cn-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text" inputMode="numeric" maxLength={5}
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter your ZIP"
                aria-label="Your ZIP code"
                className="cn-input !w-44 !pl-9"
                data-testid="zip-refine-input"
              />
            </div>
            <button type="submit" disabled={zipLoading} className="cn-btn cn-btn-primary !py-2.5 text-sm disabled:opacity-50" data-testid="zip-refine-submit">
              {zipLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find closest to me
            </button>
            {zipApplied && (
              <button type="button" onClick={clearZip} className="cn-btn cn-btn-ghost !py-2.5 text-sm" data-testid="zip-refine-clear">
                <X className="w-4 h-4" /> Clear ZIP
              </button>
            )}
          </form>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topClinicians.map((c, i) => <TopClinicianCard key={c.id} c={c} rank={i + 1} />)}
          </div>
        </section>
      )}

      {/* Specialties available locally */}
      {specialties.length > 0 && (
        <section className="cn-container pb-12">
          <h2 className="cn-display text-2xl text-cn-text mb-5">Care available in {city}</h2>
          <div className="flex flex-wrap gap-2.5">
            {specialties.map((s) => (
              <span key={s} className="px-4 py-2 rounded-full bg-cn-soft text-cn-text text-sm font-medium">{s}</span>
            ))}
          </div>
        </section>
      )}

      {/* Local clinicians */}
      <section className="cn-container pb-16">
        <h2 className="cn-display text-2xl text-cn-text mb-6">More clinicians serving {city}</h2>
        {clinicians.length === 0 ? (
          <div className="cn-card rounded-3xl p-8 text-cn-text-muted" data-testid="local-no-clinicians">
            We&rsquo;re adding clinicians in {city} now. <Link to="/auth" className="text-cn-primary font-semibold">Tell our AI assistant</Link> what you need and we&rsquo;ll find your match.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="local-clinician-grid">
            {clinicians.map((c) => <LocalClinicianCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      {/* Why CaraLoom */}
      <section className="bg-cn-primary text-white py-16">
        <div className="cn-container">
          <h2 className="cn-display text-3xl text-white mb-10">Why {city} families choose {BRAND}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { t: 'Verified & vetted', d: 'Every clinician passes identity, license, and background screening — continuously monitored.' },
              { t: 'Transparent rates', d: 'See pricing up front and book directly — no hidden agency markups.' },
              { t: 'Matched to you', d: 'Intelligent matching pairs you with the right skills, schedule, and location.' },
              { t: 'Verified-settlement protected', d: 'You only pay for care delivered, with funds secured until the visit is verified.' },
            ].map((b) => (
              <div key={b.t} className="bg-white/10 rounded-2xl p-6 border border-white/15">
                <h3 className="font-semibold text-lg mb-2">{b.t}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cn-container py-16">
        <h2 className="cn-display text-3xl text-cn-text mb-8">Home care in {city} — FAQs</h2>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`local-faq-${i}`}>
                <AccordionTrigger className="text-left text-cn-text font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-cn-text-muted leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cn-container pb-20">
        <div className="rounded-3xl bg-cn-soft/50 border border-cn-border p-10 md:p-14 text-center">
          <h2 className="cn-display text-3xl md:text-4xl text-cn-text mb-3">Find trusted care in {city} today</h2>
          <p className="text-cn-text-muted max-w-xl mx-auto mb-7">
            Get matched with verified clinicians near you — or browse {city} profiles and book the one you trust.
          </p>
          <Link to="/auth" className="cn-btn cn-btn-primary" data-testid="local-cta-bottom">Get matched <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <Footer />
      <div className="h-20" aria-hidden="true" />
      <LeadCaptureBar city={city} st={st} defaultZip={zipApplied} />
    </div>
  );
}
